import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface LocalizedName {
  [language: string]: string
}

export interface MenuCategoryNode {
  id: string
  name: LocalizedName
  image?: string
  mediaId?: number
  sortOrder: number
  subCategories: MenuCategoryNode[]
}

export interface MenuCategory {
  restaurantId: string
  categories: MenuCategoryNode[]
}

export const useMenuCategories = () => {
  const categories = ref<MenuCategoryNode[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const generateId = () => {
    return `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Transform API response format to internal MenuCategoryNode format
   * Sorts categories by sort_position
   */
  const transformApiResponse = (apiData: any[], isSubLevel = false): MenuCategoryNode[] => {
    // Sort by sort_position first
    const sortedData = [...apiData].sort((a, b) => (a.sort_position || 0) - (b.sort_position || 0))

    return sortedData.map((item) => {
      // Extract language names from localizedContent
      const localizedNames: LocalizedName = {}
      if (item.localizedContent) {
        Object.entries(item.localizedContent).forEach(([lang, content]: [string, any]) => {
          if (content && content.name) {
            localizedNames[lang] = content.name
          }
        })
      }

      // For sub-categories, check for sub_sub_categories; otherwise check for sub_categories
      const childCategories = isSubLevel && item.sub_sub_categories
        ? transformApiResponse(item.sub_sub_categories, true)
        : item.sub_categories
          ? transformApiResponse(item.sub_categories, true)
          : []

      return {
        id: item.id.toString(),
        name: localizedNames,
        image: item.media?.url || undefined,
        mediaId: item.media?.id || undefined,
        sortOrder: item.sort_position || 0,
        subCategories: childCategories,
      }
    })
  }

  /**
   * Fetch menu categories from the backend API
   */
  const fetchMenuCategories = async (restaurantId: string): Promise<void> => {
    try {
      isLoading.value = true
      error.value = null

      const response = await apiInstance.get(`/restaurant/${restaurantId}/menu-categories`)
      const data = response.data

      if (data.error === false && data.data) {
        const transformedCategories = transformApiResponse(data.data)
        categories.value = transformedCategories
      } else {
        const errorMessage = data.message || 'Failed to fetch menu categories'
        console.error('❌ Error fetching menu categories:', errorMessage)
        error.value = errorMessage
        // Keep existing categories if fetch fails
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Network error while fetching menu categories'
      console.error('❌ Error fetching menu categories:', errorMessage)
      error.value = errorMessage
      // Keep existing categories if fetch fails
    } finally {
      isLoading.value = false
    }
  }

  const reset = () => {
    categories.value = []
    isLoading.value = false
    error.value = null
  }

  const addCategory = (name: LocalizedName, mediaId?: number, parentId?: string) => {
    const newCategory: MenuCategoryNode = {
      id: generateId(),
      name,
      mediaId,
      sortOrder: 0,
      subCategories: [],
    }

    if (!parentId) {
      const sortOrder = Math.max(...categories.value.map((c) => c.sortOrder), -1) + 1
      newCategory.sortOrder = sortOrder
      categories.value.push(newCategory)
    } else {
      const parent = findCategoryById(parentId)
      if (parent) {
        const sortOrder = Math.max(...parent.subCategories.map((c) => c.sortOrder), -1) + 1
        newCategory.sortOrder = sortOrder
        parent.subCategories.push(newCategory)
      }
    }

    return newCategory
  }

  const updateCategory = (id: string, name: LocalizedName, mediaId?: number) => {
    const category = findCategoryById(id)
    if (category) {
      category.name = name
      if (mediaId !== undefined) {
        category.mediaId = mediaId
      }
    }
  }

  const deleteCategory = (id: string) => {
    const index = categories.value.findIndex((c) => c.id === id)
    if (index !== -1) {
      categories.value.splice(index, 1)
    } else {
      categories.value.forEach((category) => {
        deleteCategoryRecursive(category.subCategories, id)
      })
    }
  }

  const deleteCategoryRecursive = (items: MenuCategoryNode[], id: string) => {
    const index = items.findIndex((c) => c.id === id)
    if (index !== -1) {
      items.splice(index, 1)
    } else {
      items.forEach((item) => {
        deleteCategoryRecursive(item.subCategories, id)
      })
    }
  }

  /**
   * Determine the parent category IDs for a given category
   */
  const findCategoryParents = (categoryId: string): { mainId?: string; subId?: string } | null => {
    // Search in main categories
    for (const mainCat of categories.value) {
      if (mainCat.id === categoryId) {
        return { mainId: mainCat.id }
      }
      // Search in sub categories
      for (const subCat of mainCat.subCategories) {
        if (subCat.id === categoryId) {
          return { mainId: mainCat.id, subId: subCat.id }
        }
        // Search in sub-sub categories
        for (const subSubCat of subCat.subCategories) {
          if (subSubCat.id === categoryId) {
            return { mainId: mainCat.id, subId: subCat.id }
          }
        }
      }
    }
    return null
  }

  const findCategoryById = (id: string): MenuCategoryNode | null => {
    for (const category of categories.value) {
      if (category.id === id) return category
      const found = findInChildren(category.subCategories, id)
      if (found) return found
    }
    return null
  }

  const findInChildren = (items: MenuCategoryNode[], id: string): MenuCategoryNode | null => {
    for (const item of items) {
      if (item.id === id) return item
      const found = findInChildren(item.subCategories, id)
      if (found) return found
    }
    return null
  }

  /**
   * Determine the category type based on hierarchy level
   */
  const determineCategoryType = (parentId: string): 'main' | 'sub' | 'sub_sub' | null => {
    // Check if parentId is a main category
    for (const mainCat of categories.value) {
      if (mainCat.id === parentId) {
        return 'sub'  // Children of main category are 'sub'
      }
      // Check if parentId is a subcategory
      for (const subCat of mainCat.subCategories) {
        if (subCat.id === parentId) {
          return 'sub_sub'  // Children of sub category are 'sub_sub'
        }
      }
    }

    return null
  }

  /**
   * Save category order to API
   */
  const saveCategoryOrder = async (restaurantId: string, categoryType: 'main' | 'sub' | 'sub_sub', categoriesData: Array<{ id: string; sort_position: number }>): Promise<void> => {
    try {

      const payload = {
        type: categoryType,
        categories: categoriesData,
      }

      const response = await apiInstance.post(`/restaurant/${restaurantId}/menu-categories/sort-categories`, payload)
      const data = response.data

      if (data.error === false) {
      } else {
        const errorMessage = data.message || 'Failed to save category order'
        console.error('❌ Error saving category order:', errorMessage)
        throw new Error(errorMessage)
      }
    } catch (e: any) {
      const errorMessage = e.response?.data?.message || e.message || 'Network error while saving category order'
      console.error('❌ Error saving category order:', errorMessage)
      throw new Error(errorMessage)
    }
  }

  /**
   * Reorder main categories
   */
  const reorderCategories = async (restaurantId: string, orderedIds: string[]) => {
    try {
      // Update local state first
      const idToCategory = new Map<string, MenuCategoryNode>()
      const flattenCategories = (items: MenuCategoryNode[]) => {
        items.forEach((item) => {
          idToCategory.set(item.id, item)
          flattenCategories(item.subCategories)
        })
      }
      flattenCategories(categories.value)

      const reordered: MenuCategoryNode[] = []
      orderedIds.forEach((id) => {
        const cat = idToCategory.get(id)
        if (cat) reordered.push(cat)
      })

      const remaining = categories.value.filter((c) => !orderedIds.includes(c.id))
      categories.value = [...reordered, ...remaining]

      // Update sort positions
      const categoryData = categories.value.map((cat, index) => ({
        id: cat.id,
        sort_position: index + 1,
      }))

      // Save to API
      await saveCategoryOrder(restaurantId, 'main', categoryData)

      // Update local sortOrder
      categories.value.forEach((cat, index) => {
        cat.sortOrder = index + 1
      })
    } catch (e: any) {
      console.error('❌ Error reordering categories:', e)
      throw e
    }
  }

  /**
   * Reorder sub-categories or sub-sub-categories
   */
  const reorderSubcategories = async (restaurantId: string, parentId: string, orderedIds: string[]) => {
    try {
      const parent = findCategoryById(parentId)
      if (!parent) throw new Error('Parent category not found')

      // Determine the type based on parent's position in hierarchy
      const type = determineCategoryType(parentId)
      if (!type || type === 'main') {
        throw new Error('Invalid category type for reordering sub-categories')
      }

      // Update local state
      const reordered: MenuCategoryNode[] = []
      orderedIds.forEach((id) => {
        const child = parent.subCategories.find((c) => c.id === id)
        if (child) reordered.push(child)
      })

      const remaining = parent.subCategories.filter((c) => !orderedIds.includes(c.id))
      parent.subCategories = [...reordered, ...remaining]

      // Prepare data with new sort positions
      const categoryData = parent.subCategories.map((child, index) => ({
        id: child.id,
        sort_position: index + 1,
      }))

      // Save to API
      await saveCategoryOrder(restaurantId, type, categoryData)

      // Update local sortOrder
      parent.subCategories.forEach((child, index) => {
        child.sortOrder = index + 1
      })
    } catch (e: any) {
      console.error('❌ Error reordering sub-categories:', e)
      throw e
    }
  }

  /**
   * Upload media for a category
   */
  const uploadMedia = async (
    restaurantId: string,
    type: 'main_category' | 'sub_category' | 'sub_sub_category',
    file: File,
    typeId?: string
  ): Promise<{ id: number; original_url: string } | null> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for media upload')
        return null
      }

      if (!file) {
        console.error('🔴 File is required for media upload')
        return null
      }

      const formData = new FormData()
      formData.append('type', type)
      if (typeId) {
        formData.append('type_id', typeId)
      }
      formData.append('media[]', file)

      const response = await apiInstance.post(`/restaurant/${restaurantId}/upload-media`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const data = response.data

      if (data.error === false && data.data) {
        // Handle both formats: data.data as array or data.data.media_ids as array
        const mediaArray = Array.isArray(data.data) ? data.data : data.data.media_ids

        if (Array.isArray(mediaArray) && mediaArray.length > 0) {
          const mediaItem = mediaArray[0]
          const result = {
            id: mediaItem.id,
            original_url: mediaItem.original_url,
          }
          return result
        }
      }

      console.warn('⚠️ Upload response indicates failure or missing data:', data)
      return null
    } catch (error) {
      console.error('🔴 Error uploading category media:', error)
      throw error
    }
  }

  /**
   * Delete media for a category
   */
  const deleteMedia = async (
    restaurantId: string,
    mediaId: number,
    type: 'main_category' | 'sub_category' | 'sub_sub_category',
    typeId?: string
  ): Promise<boolean> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for media deletion')
        return false
      }

      if (!mediaId) {
        console.error('🔴 Media ID is required for media deletion')
        return false
      }

      const payload: any = {
        type,
      }

      if (typeId) {
        payload.type_id = typeId
      }

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/delete-media/${mediaId}`, {
        data: payload,
      })

      const data = response.data

      if (data.error === false || response.status === 200 || response.status === 204) {
        return true
      }

      console.warn('⚠️ Deletion response indicates failure:', data)
      return false
    } catch (error: any) {
      console.error('🔴 Error deleting category media:', error)
      throw error
    }
  }

  /**
   * Create a new category via API
   */
  const createCategoryAPI = async (
    restaurantId: string,
    localizedContent: LocalizedName,
    mainCategoryId?: string,
    subCategoryId?: string,
    mediaId?: number
  ): Promise<any | null> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for category creation')
        return null
      }

      if (!localizedContent || Object.keys(localizedContent).length === 0) {
        console.error('🔴 Localized content is required for category creation')
        return null
      }

      // Transform localizedContent from { de: "name", en: "name" } to { de: { name: "name" }, en: { name: "name" } }
      const transformedLocalizedContent: any = {}
      Object.entries(localizedContent).forEach(([lang, nameValue]) => {
        if (nameValue && nameValue.trim()) {
          transformedLocalizedContent[lang] = { name: nameValue }
        }
      })

      // Check that at least one language has content after filtering
      if (Object.keys(transformedLocalizedContent).length === 0) {
        console.error('🔴 At least one localized content entry is required for category creation')
        return null
      }

      // Build the request payload
      const payload: any = {
        localizedContent: transformedLocalizedContent,
      }

      // Determine category type and build payload accordingly
      if (mainCategoryId && subCategoryId) {
        // Creating a sub-sub-category
        payload.main_category_id = parseInt(mainCategoryId)
        payload.sub_category_id = parseInt(subCategoryId)
      } else if (mainCategoryId) {
        // Creating a sub-category
        payload.main_category_id = parseInt(mainCategoryId)
      }

      // Add media ID if provided
      if (mediaId) {
        payload.media_id = mediaId
      }

      const response = await apiInstance.post(`/restaurant/${restaurantId}/menu-categories`, payload)
      const data = response.data


      if (data.error === false && data.data) {
        return data.data
      }

      console.warn('⚠️ Create response indicates failure:', data)
      const error: any = new Error(data.message || 'Failed to create category')
      if (data.errors && typeof data.errors === 'object') {
        error.validationErrors = data.errors
      }
      throw error
    } catch (error: any) {
      console.error('🔴 Error creating category:', error)
      throw error
    }
  }

  /**
   * Edit an existing category via API
   */
  const editCategoryAPI = async (
    restaurantId: string,
    categoryId: string,
    localizedContent: LocalizedName,
    mainCategoryId?: string,
    subCategoryId?: string,
    mediaId?: number
  ): Promise<any | null> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for category edit')
        return null
      }

      if (!categoryId) {
        console.error('🔴 Category ID is required for category edit')
        return null
      }

      if (!localizedContent || Object.keys(localizedContent).length === 0) {
        console.error('🔴 Localized content is required for category edit')
        return null
      }

      // Transform localizedContent from { de: "name", en: "name" } to { de: { name: "name" }, en: { name: "name" } }
      const transformedLocalizedContent: any = {}
      Object.entries(localizedContent).forEach(([lang, nameValue]) => {
        if (nameValue && nameValue.trim()) {
          transformedLocalizedContent[lang] = { name: nameValue }
        }
      })

      // Check that at least one language has content after filtering
      if (Object.keys(transformedLocalizedContent).length === 0) {
        console.error('🔴 At least one localized content entry is required for category edit')
        return null
      }

      // Build the request payload
      const payload: any = {
        localizedContent: transformedLocalizedContent,
      }

      // Determine category type and build payload accordingly
      if (mainCategoryId && subCategoryId) {
        // This is a sub-sub-category
        payload.main_category_id = parseInt(mainCategoryId)
        payload.sub_category_id = parseInt(subCategoryId)
        payload.sub_sub_category_id = parseInt(categoryId)
      } else if (mainCategoryId) {
        // This is a sub-category
        payload.main_category_id = parseInt(mainCategoryId)
        payload.sub_category_id = parseInt(categoryId)
      } else {
        // This is a main category
        payload.main_category_id = parseInt(categoryId)
      }

      // Add media ID if provided
      if (mediaId) {
        payload.media_id = mediaId
      }

      const response = await apiInstance.put(`/restaurant/${restaurantId}/menu-categories`, payload)
      const data = response.data

      if (data.error === false && data.data) {
        return data.data
      }

      console.warn('⚠️ Edit response indicates failure:', data)
      const error: any = new Error(data.message || 'Failed to edit category')
      if (data.errors && typeof data.errors === 'object') {
        error.validationErrors = data.errors
      }
      throw error
    } catch (error: any) {
      console.error('🔴 Error editing category:', error)
      throw error
    }
  }

  /**
   * Delete an existing category via API
   */
  const deleteCategoryAPI = async (
    restaurantId: string,
    categoryId: string,
    mainCategoryId?: string,
    subCategoryId?: string
  ): Promise<boolean> => {
    try {
      if (!restaurantId) {
        console.error('🔴 Restaurant ID is required for category deletion')
        return false
      }

      if (!categoryId) {
        console.error('🔴 Category ID is required for category deletion')
        return false
      }

      // Build the request payload based on category type
      // Determine type based on which parent IDs are provided:
      // - No parents: main category
      // - mainCategoryId only: sub-category
      // - Both parents: sub-sub-category
      const payload: any = {}

      if (!mainCategoryId && !subCategoryId) {
        // Deleting a main category
        payload.main_category_id = parseInt(categoryId)
      } else if (mainCategoryId && !subCategoryId) {
        // Deleting a sub-category
        payload.main_category_id = parseInt(mainCategoryId)
        payload.sub_category_id = parseInt(categoryId)
      } else if (mainCategoryId && subCategoryId) {
        // Deleting a sub-sub-category
        payload.main_category_id = parseInt(mainCategoryId)
        payload.sub_category_id = parseInt(subCategoryId)
        payload.sub_sub_category_id = parseInt(categoryId)
      }

      const response = await apiInstance.delete(`/restaurant/${restaurantId}/menu-categories`, {
        data: payload,
      })

      const data = response.data

      if (data.error === false || response.status === 200 || response.status === 204) {
        return true
      }

      console.warn('⚠️ Delete response indicates failure:', data)
      const error: any = new Error(data.message || 'Failed to delete category')
      if (data.errors && typeof data.errors === 'object') {
        error.validationErrors = data.errors
      }
      throw error
    } catch (error: any) {
      console.error('🔴 Error deleting category:', error)

      // If the error response has a data object with a message, use that
      if (error.response?.data?.message) {
        const apiError: any = new Error(error.response.data.message)
        if (error.response.data.errors && typeof error.response.data.errors === 'object') {
          apiError.validationErrors = error.response.data.errors
        }
        throw apiError
      }

      throw error
    }
  }

  return {
    categories,
    isLoading,
    error,
    fetchMenuCategories,
    reset,
    addCategory,
    updateCategory,
    deleteCategory,
    findCategoryById,
    findCategoryParents,
    reorderCategories,
    reorderSubcategories,
    uploadMedia,
    deleteMedia,
    createCategoryAPI,
    editCategoryAPI,
    deleteCategoryAPI,
  }
}
